import React, { useState } from 'react';
import { Mail, Phone, Globe, MapPin, Briefcase, Building, Pencil, Images, Play, Trash2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { BioRichTextDisplay } from './BioMediaDisplay';
import { BrandIcons, getSocialLinks, getExtraLinks, extractYouTubeVideoId } from './BrandIcons';
import { GalleryGrid } from './GalleryLightbox';

const BioTemplateClassic = ({ card, bioUrl, constructSocialUrl, trackLinkClick, onQRClick, hideQR = false, isOwner = false, onUpdateProfileImage, onUpdateLogo, isPremium = false }) => {
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const hasGallery = card.galleryImages && card.galleryImages.length > 0;
  const hasVideos = card.youtubeVideos && card.youtubeVideos.length > 0;
  const socialLinks = getSocialLinks(card);
  const extraLinks = getExtraLinks(card);

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Cover Section */}
          <div className="relative">
            {card.backgroundImage ? (
              <div className="h-32 sm:h-40 bg-cover bg-center" style={{ backgroundImage: `url(${card.backgroundImage})` }} />
            ) : (
              <div className="h-32 sm:h-40" style={{ background: `linear-gradient(to bottom, ${card.qrColor || '#0066CC'}, white)` }} />
            )}
            
            {/* Profile Image - Large */}
            <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2">
              <div className="relative">
                {card.profileImage ? (
                  <img src={card.profileImage} alt={card.name} className="w-40 h-40 rounded-full border-4 border-white shadow-xl object-cover" />
                ) : (
                  <div className="w-40 h-40 rounded-full border-4 border-white shadow-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <span className="text-5xl font-bold text-white">{card.name?.charAt(0) || 'U'}</span>
                  </div>
                )}
                {isOwner && onUpdateProfileImage && (
                  <label htmlFor="profile-upload-classic" className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-blue-50">
                    <Pencil className="w-4 h-4 text-blue-600" />
                    <input id="profile-upload-classic" type="file" accept="image/*" onChange={onUpdateProfileImage} className="hidden" />
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="pt-24 pb-6 px-5">
            {/* Name & Title */}
            <div className="text-center mb-5">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{card.name}</h1>
              {card.title && <p className="text-gray-600 text-sm mt-1 flex items-center justify-center gap-1"><Briefcase className="w-3.5 h-3.5" />{card.title}</p>}
              {card.company && <p className="text-gray-500 text-xs flex items-center justify-center gap-1"><Building className="w-3 h-3" />{card.company}</p>}
            </div>

            {/* Contact Info */}
            <div className="space-y-2 mb-5">
              {card.phone && (
                <a href={`tel:${card.phone}`} onClick={() => trackLinkClick('phone', card.phone)} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700 text-sm">{card.phone}</span>
                </a>
              )}
              {card.email && (
                <a href={`mailto:${card.email}`} onClick={() => trackLinkClick('email', card.email)} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700 text-sm break-all">{card.email}</span>
                </a>
              )}
              {(card.website || (card.websiteName && card.websiteTLD)) && (
                <a href={card.website || `https://${card.websiteName}${card.websiteTLD}`} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('website', card.website)} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <Globe className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700 text-sm break-all">{(card.website || `${card.websiteName}${card.websiteTLD}`).replace(/^https?:\/\//, '')}</span>
                </a>
              )}
              {(card.location || card.locationAddress) && (
                <a href={
                  card.location && (card.location.startsWith('http://') || card.location.startsWith('https://'))
                    ? card.location 
                    : `https://maps.google.com/?q=${encodeURIComponent(card.locationAddress || card.location || '')}`
                } target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('location', card.location)} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-700 text-sm">{card.locationAddress || card.location || 'View Location'}</span>
                </a>
              )}
            </div>

            {/* Social Links - Brand Icons */}
            {socialLinks.length > 0 && (
              <div className="flex justify-center flex-wrap gap-3 mb-5">
                {socialLinks.map((social, index) => {
                  const BrandIcon = BrandIcons[social.type];
                  return (
                    <a key={index} href={constructSocialUrl(social.type, social.value)} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick(social.type, social.value)} className="w-11 h-11 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-all hover:scale-110" title={social.label}>
                      {BrandIcon && <BrandIcon size={20} />}
                    </a>
                  );
                })}
              </div>
            )}

            {/* Extra Links */}
            {extraLinks.length > 0 && (
              <div className="space-y-2 mb-5">
                {extraLinks.map((link, index) => (
                  <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('extraLink', link.url)} className="flex items-center gap-3 p-2.5 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200">
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700 text-sm font-medium">{link.title}</span>
                  </a>
                ))}
              </div>
            )}

            {/* Rich Bio - Collapsible */}
            {card.richBio && (
              <div className="bg-gray-50 rounded-xl p-4 mb-5 border border-gray-100">
                <div className={`overflow-hidden transition-all duration-300 text-sm text-gray-600 ${!isBioExpanded ? 'max-h-12' : 'max-h-[2000px]'}`}>
                  <BioRichTextDisplay content={card.richBio} />
                </div>
                <button onClick={() => setIsBioExpanded(!isBioExpanded)} className="flex items-center gap-1 mt-2 text-xs font-medium text-blue-600 hover:text-blue-700">
                  {isBioExpanded ? <><ChevronUp className="w-3.5 h-3.5" />Show less</> : <><ChevronDown className="w-3.5 h-3.5" />Read more</>}
                </button>
              </div>
            )}

            {/* Gallery */}
            {hasGallery && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Images className="w-4 h-4 text-gray-600" />
                  <h3 className="text-sm font-semibold text-gray-700">Gallery</h3>
                </div>
                <GalleryGrid 
                  images={card.galleryImages} 
                  onDeleteImage={card.onDeleteImage} 
                  isOwner={isOwner}
                  accentColor="#f59e0b"
                />
              </div>
            )}

            {/* Videos */}
            {hasVideos && (
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <Play className="w-4 h-4 text-gray-600" />
                  <h3 className="text-sm font-semibold text-gray-700">Videos</h3>
                </div>
                <div className="space-y-2">
                  {card.youtubeVideos.slice(0, 3).map((videoId, index) => {
                    const cleanVideoId = extractYouTubeVideoId(videoId);
                    return (
                      <div key={index} className="relative aspect-video rounded-lg overflow-hidden group">
                        <iframe src={`https://www.youtube.com/embed/${cleanVideoId}?rel=0`} title={`Video ${index + 1}`} className="w-full h-full" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        {isOwner && card.onDeleteVideo && (
                          <button onClick={() => card.onDeleteVideo(index)} className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BioTemplateClassic;
